/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2024
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { IDetailAggregationData } from '../../../../shared/DetailSummaryComponent/DetailSummaryComponent';
import {
  ConversionType,
  getConvertedValue,
  getMaxConvertedValueFromArray
} from '../../../../../main';
import _ from 'lodash';
import descriptors from '../../../../../locales/en-US/descriptors';
import { IMessageDescriptor } from '@scuf/localization/dist/types';
import { IEnergyOptimizationChartDataProps } from '../../EnergyOptimizationInterfaces';

export enum EOUtilityTabNameEnum {
  All = 'All',
  Electricity = 'Electricity',
  Gas = 'Gas',
  Steam = 'Steam',
  HotWater = 'HotWater',
  ChilledWater = 'ChilledWater'
}

export const getDescriptorForEOUtilityTabNameEnum = (
  id: EOUtilityTabNameEnum
): IMessageDescriptor | string => {
  const d = descriptors.energyManagement;
  switch (id) {
    case EOUtilityTabNameEnum.All:
      return d.EOUtility_all;
    case EOUtilityTabNameEnum.Electricity:
      return d.EOUtility_electricity;
    case EOUtilityTabNameEnum.Gas:
      return d.EOUtility_gas;
    case EOUtilityTabNameEnum.HotWater:
      return d.EOUtility_hotwater;
    case EOUtilityTabNameEnum.ChilledWater:
      return d.EOUtility_chilledwater;
    case EOUtilityTabNameEnum.Steam:
      return d.EOUtility_steam;
    default:
      return _.capitalize(_.toString(id));
  }
};

export const eoScalingAndConversionForUtilityConsumption = (
  aggregationData: IDetailAggregationData,
  chartData: IEnergyOptimizationChartDataProps,
  utilityType: string,
  fromUnit: string,
  userPreferedUnit?: string
) => {
  const { actualCost, baselineCost, costSavings } = aggregationData;
  const valuesArray = [actualCost, baselineCost];
  let conversionType = ConversionType.ELECTRICITY;
  switch (utilityType) {
    case 'gas':
      conversionType = ConversionType.GAS;
      break;
    case 'steam':
      conversionType = ConversionType.STEAM;
      break;
    case 'water':
      conversionType = ConversionType.WATER;
      break;
    default:
      conversionType = ConversionType.ELECTRICITY;
  }
  if (utilityType === 'gas') {
    conversionType = ConversionType.GAS;
  }
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    conversionType,
    userPreferedUnit || fromUnit,
    [actualCost],
    fromUnit
  );
  const costSavingsUnitConversion = getMaxConvertedValueFromArray(
    conversionType,
    userPreferedUnit || fromUnit,
    [costSavings],
    fromUnit
  );
  const baselineCostUnitConversion = getMaxConvertedValueFromArray(
    conversionType,
    userPreferedUnit || fromUnit,
    [baselineCost],
    fromUnit
  );
  const chartDataUnit = getMaxConvertedValueFromArray(
    conversionType,
    userPreferedUnit || fromUnit,
    [
      ...(chartData.actualConsumption
        ? chartData.actualConsumption.map((con) => con[1] || 0)
        : []),
      ...(chartData.energySavings
        ? chartData.energySavings.map((con) => con[1] || 0)
        : [])
    ],
    fromUnit
  );
  const actualConsumption =
    chartData.actualConsumption &&
    chartData.actualConsumption.map((con) => [
      con[0],
      getConvertedValue(
        conversionType,
        userPreferedUnit || fromUnit,
        con[1],
        chartDataUnit.Unit
      ).Value || 0
    ]);
  const energySavings =
    chartData.energySavings &&
    chartData.energySavings.map((sav) => [
      sav[0],
      getConvertedValue(
        conversionType,
        userPreferedUnit || fromUnit,
        sav[1],
        chartDataUnit.Unit
      ).Value || 0
    ]);
  const inactiveDays =
    chartData.inactiveDays &&
    chartData.inactiveDays.map((inac) => ({
      ...inac,
      y:
        getConvertedValue(
          conversionType,
          userPreferedUnit || fromUnit,
          inac.y,
          chartDataUnit.Unit
        ).Value || 0
    }));
  const runTimeEOStatus =
    chartData.runTimeEOStatus && chartData.runTimeEOStatus.length > 0
      ? chartData.runTimeEOStatus.map((status) => {
          return status;
        })
      : [];
  const excludedDays =
    chartData.excludedDays &&
    chartData.excludedDays.map((exc) => ({
      ...exc,
      y:
        getConvertedValue(
          conversionType,
          userPreferedUnit || fromUnit,
          exc.y,
          chartDataUnit.Unit
        ).Value || 0
    }));
  return {
    conversionType,
    actualCost: getConvertedValue(
      conversionType,
      userPreferedUnit || fromUnit,
      actualCost,
      maxValueUnitConversion.Unit
    ).Value,
    baselineCost: getConvertedValue(
      conversionType,
      userPreferedUnit || fromUnit,
      baselineCost,
      baselineCostUnitConversion.Unit
    ).Value,
    costSavings: getConvertedValue(
      conversionType,
      userPreferedUnit || fromUnit,
      costSavings,
      costSavingsUnitConversion.Unit
    ).Value,
    measurementUnit: maxValueUnitConversion.Unit,
    chartDataUnit: chartDataUnit.Unit,
    costSavingsUnit: costSavingsUnitConversion.Unit,
    baselineCostUnit: baselineCostUnitConversion.Unit,
    actualConsumption,
    energySavings,
    inactiveDays,
    excludedDays,
    runTimeEOStatus
  };
};
